import { useMemo, useCallback } from 'react';
import { SConfirmDialog } from '@setvi/shared/components';
import { useDialog } from '@setvi/shared/providers';
import { useQuotePricingCalculations } from 'pages/quotes/components/quote-items/components/product/hooks/use-quote-pricing-calculations';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';
import {
  DeleteIconV2,
  DocumentIconV2,
  RefreshIcon,
  MoveIcon,
  FillBagOutlineIcon,
  AddBySKUOutlineIcon,
  AddSPOOutlineIcon,
  AddSectionIcon,
  EditIcon
} from '@setvi/shared/components/sicons';
import AccountTreeOutlinedIcon from '@material-ui/icons/AccountTreeOutlined';
import {
  useQuoteData,
  useQuoteItemsData,
  useQuoteMutations,
  useQuoteStatusFlags
} from '../../../../context';
import {
  useFieldLock,
  useItemAnyFieldLock,
  usePricingLock
} from '../../../../pages/details/realtime/use-field-locks';
import { AddNoteDialog } from '../add-note-dialog';
import { EditLabelDialog } from '../edit-label-dialog';
import { AddBySku } from '../../../add-by-sku';
import { AddSPO } from '../../../add-spo';
import { AddFromCatalogDialog } from '../../../add-from-catalog-dialog';
import { SectionDialog } from '../../../add-section-dialog';

const MENU_ICONS = {
  refresh: <RefreshIcon fontSize="small" />,
  document: <DocumentIconV2 fontSize="small" />,
  edit: <EditIcon fontSize="small" />,
  options: (
    <AccountTreeOutlinedIcon fontSize="small" style={{ color: '#5D7083' }} />
  ),
  catalog: <FillBagOutlineIcon fontSize="small" />,
  sku: <AddBySKUOutlineIcon fontSize="small" />,
  spo: <AddSPOOutlineIcon fontSize="small" />,
  move: <MoveIcon fontSize="small" />,
  addSection: <AddSectionIcon fontSize="small" />,
  delete: <DeleteIconV2 fontSize="small" />
};

export const useProductMenuItems = (product: QuoteProductDTO) => {
  const { openDialog, closeDialog } = useDialog();
  const { quote, currentStatus } = useQuoteData();
  const { items: quoteItems } = useQuoteItemsData();
  const {
    updateItem,
    updateItemQuantity,
    updateItemNote,
    updateItemLabels,
    removeItem,
    moveItem,
    addSubitems,
    addFreehandSubitem,
    addSectionAndMoveItem,
    refreshSinglePrice
  } = useQuoteMutations();
  const { isAddingSubitems, isAddingFreehandSubitem } = useQuoteStatusFlags();

  const isQuoteEditable =
    !!currentStatus?.isQuoteEditable && !!quote?.acl?.canEditQuote;

  const productId = product?.id || product?._id;
  const segmentId = quote?.segment?._id;

  // Collaborative edit locks: held while THIS user has the matching dialog open,
  // so other collaborators see that entry disabled. Each surface locks on its
  // own — `note` (Edit Note), `label` (Add/Edit Label) and `options` (Add
  // Options: catalog / SKU / SPO) are independent.
  const noteLock = useFieldLock(productId, 'note');
  const labelLock = useFieldLock(productId, 'label');
  const optionsLock = useFieldLock(productId, 'options');
  const removeLock = useFieldLock(productId, 'remove');
  // Refresh Price recomputes the unit price/GP, so block it while another
  // collaborator is editing any of this item's pricing inputs.
  const pricingLock = usePricingLock(productId);
  const pricingLockTooltip = pricingLock.lockedByOther
    ? `${pricingLock.holder?.name || 'Another user'} is editing the price`
    : undefined;
  // Block Remove while another collaborator is working on this item in ANY way
  // (pricing inputs, note, label, options, or an open Remove dialog).
  const itemBusy = useItemAnyFieldLock(productId);
  const itemBusyTooltip = itemBusy.lockedByOther
    ? `${itemBusy.holder?.name || 'Another user'} is editing this item`
    : undefined;
  const noteLockTooltip = noteLock.lockedByOther
    ? `${noteLock.holder?.name || 'Another user'} is editing the note`
    : undefined;
  const labelLockTooltip = labelLock.lockedByOther
    ? `${labelLock.holder?.name || 'Another user'} is editing the label`
    : undefined;
  const optionsLockTooltip = optionsLock.lockedByOther
    ? `${optionsLock.holder?.name || 'Another user'} is editing the options`
    : undefined;

  const parentSectionId = useMemo(() => {
    if (!quoteItems) return null;

    for (const item of quoteItems) {
      if (item.type === 'section' && 'children' in item) {
        const hasProduct = (item.children as QuoteProductDTO[])?.some(
          (child: QuoteProductDTO) => (child.id || child._id) === productId
        );
        if (hasProduct) {
          return item.id || item._id;
        }
      }
    }
    return null;
  }, [quoteItems, productId]);

  const availableSections = useMemo(
    () =>
      quoteItems
        ?.filter(item => {
          if (item.type !== 'section') return false;
          const sectionId = item.id || item._id;
          return sectionId !== parentSectionId;
        })
        .map(section => ({
          id: section.id || section._id,
          name: section.name || 'Unnamed Section'
        })) || [],
    [quoteItems, parentSectionId]
  );

  const handlePricingUpdate = useCallback(
    (itemID: string, updates: any) => {
      if ('quantity' in updates && Object.keys(updates).length === 1) {
        updateItemQuantity.mutate({
          itemId: itemID,
          value: updates.quantity
        });
      } else {
        updateItem.mutate({ itemId: itemID, updates });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {
    handleQuantityChange,
    handleSellChange,
    handleProfitMarginChange,
    handleProfitMarginPercentageChange
  } = useQuotePricingCalculations({ updatePricing: handlePricingUpdate });

  const handleOpenAddNoteDialog = useCallback(() => {
    if (noteLock.lockedByOther) return;
    noteLock.acquire();
    // Release on every close path: the explicit close button calls the prop
    // onClose, while backdrop/escape closes call the option's onClose.
    const releaseNote = () => noteLock.release();
    const handleClose = () => {
      releaseNote();
      closeDialog(`add-note-${productId}`);
    };
    openDialog(
      <AddNoteDialog
        itemId={productId}
        itemName={product.name}
        note={product?.note}
        onClose={handleClose}
        onSave={(id, newNote) => {
          updateItemNote.mutate({ itemId: id, note: newNote });
        }}
      />,
      { id: `add-note-${productId}`, onClose: releaseNote }
    );
  }, [
    openDialog,
    closeDialog,
    productId,
    product.name,
    product?.note,
    updateItemNote,
    noteLock
  ]);

  const handleLabelDialog = useCallback(() => {
    if (labelLock.lockedByOther) return;
    labelLock.acquire();
    const releaseLabel = () => labelLock.release();
    const handleClose = () => {
      releaseLabel();
      closeDialog(`edit-label-${productId}`);
    };
    openDialog(
      <EditLabelDialog
        itemId={productId}
        labelType={product.labelType || null}
        customLabel={product.customLabel}
        onClose={handleClose}
        onSave={(id, data) => {
          updateItemLabels.mutate({
            itemId: id,
            labelType: data.labelType,
            customLabel: data.customLabel,
            includeInTotalCost: product.includeInTotalCost ?? true
          });
        }}
      />,
      { id: `edit-label-${productId}`, onClose: releaseLabel }
    );
  }, [
    openDialog,
    closeDialog,
    productId,
    product.labelType,
    product.customLabel,
    product.includeInTotalCost,
    updateItemLabels,
    labelLock
  ]);

  const handleAddToNewSection = useCallback(() => {
    openDialog(
      <SectionDialog
        onClose={() => closeDialog(`add-section-move-${productId}`)}
        addSection={params => {
          addSectionAndMoveItem({
            ...params,
            itemId: productId
          });
          closeDialog(`add-section-move-${productId}`);
        }}
      />,
      { id: `add-section-move-${productId}` }
    );
  }, [openDialog, closeDialog, productId, addSectionAndMoveItem]);

  const handleRemoveItem = useCallback(() => {
    if (removeLock.lockedByOther) return;
    removeLock.acquire();
    const releaseRemove = () => removeLock.release();
    const handleClose = () => {
      releaseRemove();
      closeDialog(`remove-item-${productId}`);
    };
    openDialog(
      <SConfirmDialog
        type="delete"
        title="Remove Item from the Quote"
        description="This will permanently remove this item from the quote."
        additionalMessage="This action CANNOT be undone."
        onClose={handleClose}
        onConfirm={() => {
          removeItem(productId);
          handleClose();
        }}
        confirmText="Remove"
        closeText="Cancel"
      />,
      { id: `remove-item-${productId}`, onClose: releaseRemove }
    );
  }, [openDialog, closeDialog, productId, removeItem, removeLock]);

  const handleAddFromCatalog = useCallback(() => {
    if (optionsLock.lockedByOther) return;
    optionsLock.acquire();
    const releaseOptions = () => optionsLock.release();
    const handleClose = () => {
      releaseOptions();
      closeDialog(`add-from-catalog-${productId}`);
    };
    openDialog(
      <AddFromCatalogDialog
        onClose={handleClose}
        parentId={productId}
        quote={quote as any}
        addProducts={({ products, parentId }) =>
          addSubitems({
            itemId: parentId || productId,
            products
          })
        }
      />,
      { id: `add-from-catalog-${productId}`, onClose: releaseOptions }
    );
  }, [openDialog, closeDialog, productId, quote, addSubitems, optionsLock]);

  const handleAddBySku = useCallback(() => {
    if (optionsLock.lockedByOther) return;
    optionsLock.acquire();
    const releaseOptions = () => optionsLock.release();
    const handleClose = () => {
      releaseOptions();
      closeDialog(`add-by-sku-${productId}`);
    };
    openDialog(
      <AddBySku
        onClose={handleClose}
        addProducts={products =>
          addSubitems({
            itemId: productId,
            products: products.products
          })
        }
        isAddingProducts={isAddingSubitems}
        selectedCustomer={quote?.account as any}
        segmentId={segmentId}
      />,
      { id: `add-by-sku-${productId}`, onClose: releaseOptions }
    );
  }, [
    openDialog,
    isAddingSubitems,
    quote?.account,
    segmentId,
    productId,
    closeDialog,
    addSubitems,
    optionsLock
  ]);

  const handleAddSpo = useCallback(() => {
    if (optionsLock.lockedByOther) return;
    optionsLock.acquire();
    const releaseOptions = () => optionsLock.release();
    const handleClose = () => {
      releaseOptions();
      closeDialog(`add-spo-${productId}`);
    };
    openDialog(
      <AddSPO
        onClose={handleClose}
        onSubmit={async spoData => {
          await addFreehandSubitem({
            itemId: productId,
            ...spoData
          });
          handleClose();
        }}
        isAddingProducts={isAddingFreehandSubitem}
      />,
      { id: `add-spo-${productId}`, onClose: releaseOptions }
    );
  }, [
    openDialog,
    closeDialog,
    productId,
    addFreehandSubitem,
    isAddingFreehandSubitem,
    optionsLock
  ]);

  const handleRefreshPrice = useCallback(() => {
    if (product?.sku) {
      refreshSinglePrice({
        productId,
        sku: product.sku
      });
    }
  }, [product?.sku, productId, refreshSinglePrice]);

  const menuItems = useMemo(() => {
    if (!isQuoteEditable) return [];

    return [
      {
        color: '#333D47',
        icon: MENU_ICONS.refresh,
        text: 'Refresh Price',
        // Non-editable catalog items can't be re-priced (freehand items never
        // refresh either, and an item with no SKU has nothing to look up).
        // Also blocked while another user edits this item's price/GP.
        disabled:
          product?.isFreeHand ||
          !product?.sku ||
          !product?.isEditable ||
          pricingLock.lockedByOther,
        tooltipText: pricingLockTooltip,
        onClick: handleRefreshPrice
      },
      {
        color: '#333D47',
        icon: MENU_ICONS.document,
        text: product?.note?.trim() ? 'Edit Note' : 'Add Note',
        disabled: noteLock.lockedByOther,
        tooltipText: noteLockTooltip,
        onClick: handleOpenAddNoteDialog
      },
      ...(product?.children?.length
        ? [
            {
              color: '#333D47',
              icon: MENU_ICONS.edit,
              text: product?.labelType ? 'Edit Label' : 'Add Label',
              disabled: labelLock.lockedByOther,
              tooltipText: labelLockTooltip,
              onClick: handleLabelDialog
            }
          ]
        : []),
      {
        color: '#333D47',
        icon: MENU_ICONS.options,
        text: 'Add Options',
        disabled: optionsLock.lockedByOther,
        tooltipText: optionsLockTooltip,
        submenu: [
          {
            icon: MENU_ICONS.catalog,
            text: 'Add From Catalog',
            color: '#333D47',
            onClick: handleAddFromCatalog
          },
          {
            icon: MENU_ICONS.sku,
            text: 'Add by SKU',
            color: '#333D47',
            onClick: handleAddBySku
          },
          {
            icon: MENU_ICONS.spo,
            text: 'Add SPO',
            color: '#333D47',
            onClick: handleAddSpo
          }
        ]
      },
      {
        color: '#333D47',
        icon: MENU_ICONS.move,
        text: 'Move to section',
        submenu: [
          ...availableSections.map(section => ({
            text: section.name,
            color: '#333D47',
            onClick: () => moveItem(productId, section.id)
          })),
          {
            icon: MENU_ICONS.addSection,
            text: 'Add to new section',
            color: '#333D47',
            onClick: handleAddToNewSection
          },
          ...(parentSectionId
            ? [
                {
                  text: 'No section (remove)',
                  color: '#333D47',
                  onClick: () => moveItem(productId, null)
                }
              ]
            : [])
        ]
      },
      {
        icon: MENU_ICONS.delete,
        color: 'danger',
        text: 'Remove Item',
        disabled: itemBusy.lockedByOther,
        tooltipText: itemBusyTooltip,
        onClick: handleRemoveItem
      }
    ];
  }, [
    isQuoteEditable,
    product?.isFreeHand,
    product?.sku,
    product?.isEditable,
    product?.note,
    product?.children?.length,
    product?.labelType,
    noteLock.lockedByOther,
    noteLockTooltip,
    labelLock.lockedByOther,
    labelLockTooltip,
    optionsLock.lockedByOther,
    optionsLockTooltip,
    pricingLock.lockedByOther,
    pricingLockTooltip,
    itemBusy.lockedByOther,
    itemBusyTooltip,
    handleRefreshPrice,
    handleOpenAddNoteDialog,
    handleLabelDialog,
    handleAddFromCatalog,
    handleAddBySku,
    handleAddSpo,
    availableSections,
    moveItem,
    productId,
    handleAddToNewSection,
    parentSectionId,
    handleRemoveItem
  ]);

  return {
    menuItems,
    isQuoteEditable,
    handleQuantityChange,
    handleSellChange,
    handleProfitMarginChange,
    handleProfitMarginPercentageChange
  };
};
